import React, { useEffect, useState } from "react";

import {
    RiseOutlined,
    SaveOutlined,
    DeleteOutlined,
    ArrowLeftOutlined,
    UserOutlined
} from "@ant-design/icons";

import {
    canSyncRehabData,
    getActiveRehabProfileId,
    getRehabHistory,
    saveRehabData,
    deleteRehabData
} from "../../api/rehabTracking";

import {
    getPatientProfiles
} from "../../api/patientProfile";


export default function Rom() {

    const [joint, setJoint] = useState("vai");
    const [side, setSide] = useState("phai");
    const [movement, setMovement] = useState("gap");

    const [activeRom, setActiveRom] = useState("");
    const [passiveRom, setPassiveRom] = useState("");

    const [painPresent, setPainPresent] = useState(false);
    const [painAngle, setPainAngle] = useState("");

    const [position, setPosition] = useState("");
    const [measuringDevice, setMeasuringDevice] = useState("");
    const [note, setNote] = useState("");

    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [activeProfile, setActiveProfile] = useState(null);


    const getJointLabel = (value) => {

        switch (value) {

            case "vai":
                return "Khớp vai";

            case "khuyu":
                return "Khớp khuỷu";

            case "co-tay":
                return "Khớp cổ tay";

            case "hang":
                return "Khớp háng";

            case "goi":
                return "Khớp gối";

            case "co-chan":
                return "Khớp cổ chân";

            default:
                return "Khớp khác";
        }
    };


    const getSideLabel = (value) => {

        switch (value) {

            case "phai":
                return "Bên phải";

            case "trai":
                return "Bên trái";

            case "hai-ben":
                return "Hai bên";

            default:
                return "";
        }
    };


    const movementOptions = {

        vai: [
            { value: "gap", label: "Gấp vai" },
            { value: "duoi", label: "Duỗi vai" },
            { value: "dang", label: "Dạng vai" },
            { value: "khep", label: "Khép vai" },
            { value: "xoay-trong", label: "Xoay trong" },
            { value: "xoay-ngoai", label: "Xoay ngoài" }
        ],

        khuyu: [
            { value: "gap", label: "Gấp khuỷu" },
            { value: "duoi", label: "Duỗi khuỷu" }
        ],

        "co-tay": [
            { value: "gap", label: "Gấp cổ tay" },
            { value: "duoi", label: "Duỗi cổ tay" },
            { value: "nghieng-tru", label: "Nghiêng trụ" },
            { value: "nghieng-quay", label: "Nghiêng quay" }
        ],

        hang: [
            { value: "gap", label: "Gấp háng" },
            { value: "duoi", label: "Duỗi háng" },
            { value: "dang", label: "Dạng háng" },
            { value: "khep", label: "Khép háng" },
            { value: "xoay-trong", label: "Xoay trong" },
            { value: "xoay-ngoai", label: "Xoay ngoài" }
        ],

        goi: [
            { value: "gap", label: "Gấp gối" },
            { value: "duoi", label: "Duỗi gối" }
        ],

        "co-chan": [
            { value: "gap-mu", label: "Gấp mu" },
            { value: "gap-long", label: "Gấp lòng" },
            { value: "nghieng-trong", label: "Nghiêng trong" },
            { value: "nghieng-ngoai", label: "Nghiêng ngoài" }
        ]
    };


    const getMovementLabel = (
        jointValue,
        movementValue
    ) => {

        const list =
            movementOptions[jointValue] || [];

        const found =
            list.find(
                (item) =>
                    item.value === movementValue
            );

        return (
            found?.label ||
            movementValue ||
            ""
        );
    };


    const handleJointChange = (value) => {

        setJoint(value);

        const firstMovement =
            movementOptions[value]?.[0]?.value || "";

        setMovement(firstMovement);
    };


    const formatDateTime = (value) => {

        if (!value) {

            return {
                date: "",
                time: ""
            };
        }


        const dateObject = new Date(value);


        if (Number.isNaN(dateObject.getTime())) {

            return {
                date: "",
                time: ""
            };
        }


        return {

            date:
                dateObject.toLocaleDateString("vi-VN"),

            time:
                dateObject.toLocaleTimeString(
                    "vi-VN",
                    {
                        hour: "2-digit",
                        minute: "2-digit"
                    }
                )
        };
    };


    const parseNullableNumber = (value) => {

        if (
            value === "" ||
            value === null ||
            typeof value === "undefined"
        ) {
            return null;
        }

        const number = Number(value);

        if (Number.isNaN(number)) {
            return null;
        }

        return number;
    };


    const loadActiveProfile = async () => {

        try {

            if (!canSyncRehabData()) {

                setActiveProfile(null);
                return;
            }


            const activeId =
                getActiveRehabProfileId();


            if (!activeId) {

                setActiveProfile(null);
                return;
            }


            const profiles =
                await getPatientProfiles();


            const found =
                Array.isArray(profiles)
                    ? profiles.find(
                        (item) =>
                            Number(item.profile_id) ===
                            Number(activeId)
                    )
                    : null;


            setActiveProfile(
                found || null
            );

        } catch (error) {

            console.error(
                "Không thể tải hồ sơ:",
                error
            );

            setActiveProfile(null);
        }
    };


    const loadHistory = async () => {

        setLoading(true);
        setMessage("");


        try {

            if (!canSyncRehabData()) {

                setHistory([]);

                setMessage(
                    "Vui lòng đăng nhập để lưu và xem dữ liệu phục hồi chức năng."
                );

                return;
            }


            if (!getActiveRehabProfileId()) {

                setHistory([]);

                setMessage(
                    "Vui lòng chọn hồ sơ đang theo dõi trước khi sử dụng công cụ."
                );

                return;
            }


            const data =
                await getRehabHistory("rom");


            const formatted =
                Array.isArray(data)
                    ? data.map((item) => {

                        const tracking =
                            item.tracking_data || {};

                        const dateTime =
                            formatDateTime(
                                item.created_at
                            );


                        /*
                         * Hỗ trợ dữ liệu ROM cũ:
                         * trước đây chỉ có tracking.degree
                         */
                        const legacyDegree =
                            parseNullableNumber(
                                tracking.degree
                            );

                        const activeValue =
                            parseNullableNumber(
                                tracking.activeRom ??
                                tracking.active_rom_deg
                            );

                        const passiveValue =
                            parseNullableNumber(
                                tracking.passiveRom ??
                                tracking.passive_rom_deg
                            );


                        return {

                            id:
                                item.health_tracking_id,

                            joint:
                                tracking.joint || "",

                            jointLabel:
                                tracking.jointLabel ||
                                tracking.joint_label ||
                                getJointLabel(
                                    tracking.joint
                                ),

                            side:
                                tracking.side || "",

                            sideLabel:
                                tracking.sideLabel ||
                                tracking.side_label ||
                                getSideLabel(
                                    tracking.side
                                ),

                            movement:
                                tracking.movement || "",

                            movementLabel:
                                tracking.movementLabel ||
                                tracking.movement_label ||
                                getMovementLabel(
                                    tracking.joint,
                                    tracking.movement
                                ),

                            activeRom:
                                activeValue,

                            passiveRom:
                                passiveValue,

                            legacyDegree:
                                legacyDegree,

                            painPresent:
                                Boolean(
                                    tracking.painPresent ??
                                    tracking.pain_present ??
                                    false
                                ),

                            painAngle:
                                parseNullableNumber(
                                    tracking.painAngle ??
                                    tracking.pain_angle_deg
                                ),

                            position:
                                tracking.position || "",

                            measuringDevice:
                                tracking.measuringDevice ||
                                tracking.measuring_device ||
                                "",

                            note:
                                tracking.note || "",

                            date:
                                tracking.date ||
                                dateTime.date,

                            time:
                                tracking.time ||
                                dateTime.time
                        };
                    })
                    : [];


            setHistory(formatted);

        } catch (error) {

            console.error(
                "Không thể tải lịch sử ROM:",
                error
            );

            setHistory([]);

            setMessage(
                error?.message ||
                "Không thể tải lịch sử ROM."
            );

        } finally {

            setLoading(false);
        }
    };


    useEffect(() => {

        loadActiveProfile();
        loadHistory();

    }, []);


    const resetForm = () => {

        setActiveRom("");
        setPassiveRom("");

        setPainPresent(false);
        setPainAngle("");

        setPosition("");
        setMeasuringDevice("");
        setNote("");
    };


    const validateDegree = (
        value,
        fieldName
    ) => {

        if (value === "") {
            return true;
        }


        const number =
            Number(value);


        if (
            Number.isNaN(number) ||
            number < 0 ||
            number > 360
        ) {

            alert(
                `${fieldName} phải là số từ 0 đến 360 độ.`
            );

            return false;
        }


        return true;
    };


    const handleSave = async () => {

        if (!canSyncRehabData()) {

            alert(
                "Vui lòng đăng nhập trước khi lưu dữ liệu."
            );

            return;
        }


        if (!getActiveRehabProfileId()) {

            alert(
                "Vui lòng chọn hồ sơ đang theo dõi trước."
            );

            return;
        }


        if (
            activeRom === "" &&
            passiveRom === ""
        ) {

            alert(
                "Vui lòng nhập ít nhất ROM chủ động hoặc ROM thụ động."
            );

            return;
        }


        if (
            !validateDegree(
                activeRom,
                "ROM chủ động"
            )
        ) {
            return;
        }


        if (
            !validateDegree(
                passiveRom,
                "ROM thụ động"
            )
        ) {
            return;
        }


        if (
            painPresent &&
            painAngle !== "" &&
            !validateDegree(
                painAngle,
                "Góc xuất hiện đau"
            )
        ) {
            return;
        }


        const now =
            new Date();


        const trackingData = {

            joint,

            jointLabel:
                getJointLabel(joint),

            side,

            sideLabel:
                getSideLabel(side),

            movement,

            movementLabel:
                getMovementLabel(
                    joint,
                    movement
                ),

            activeRom:
                activeRom === ""
                    ? null
                    : Number(activeRom),

            passiveRom:
                passiveRom === ""
                    ? null
                    : Number(passiveRom),

            painPresent,

            painAngle:
                painPresent &&
                painAngle !== ""
                    ? Number(painAngle)
                    : null,

            position:
                position.trim(),

            measuringDevice:
                measuringDevice.trim(),

            note:
                note.trim(),

            date:
                now.toLocaleDateString(
                    "vi-VN"
                ),

            time:
                now.toLocaleTimeString(
                    "vi-VN",
                    {
                        hour: "2-digit",
                        minute: "2-digit"
                    }
                )
        };


        setSaving(true);
        setMessage("");


        try {

            await saveRehabData(
                "rom",
                trackingData
            );


            resetForm();

            await loadHistory();


            setMessage(
                "Đã lưu kết quả ROM."
            );

        } catch (error) {

            console.error(
                "Không thể lưu ROM:",
                error
            );


            setMessage(
                error?.message ||
                "Không thể lưu kết quả ROM."
            );

        } finally {

            setSaving(false);
        }
    };


    const handleDelete = async (id) => {

        const confirmed =
            window.confirm(
                "Bạn có chắc muốn xóa kết quả ROM này?"
            );


        if (!confirmed) {
            return;
        }


        try {

            await deleteRehabData(id);


            setHistory(
                (current) =>
                    current.filter(
                        (item) =>
                            Number(item.id) !==
                            Number(id)
                    )
            );


            setMessage(
                "Đã xóa kết quả ROM."
            );

        } catch (error) {

            console.error(
                "Không thể xóa ROM:",
                error
            );


            setMessage(
                error?.message ||
                "Không thể xóa kết quả ROM."
            );
        }
    };


    return (

        <div className="max-w-screen-xl mx-auto px-3 md:px-5 pt-16 md:pt-4 pb-10">


            {/* QUAY LẠI */}

            <a
                href="/tienich/phuc-hoi-chuc-nang"
                className="inline-flex items-center text-emerald-600 font-semibold text-sm mb-4"
            >

                <ArrowLeftOutlined className="mr-2" />

                Phục hồi chức năng

            </a>


            {/* HEADER */}

            <div className="bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl px-5 py-5 md:p-8 text-white">

                <div className="flex items-center">

                    <div className="w-12 h-12 rounded-xl bg-white bg-opacity-20 flex items-center justify-center text-2xl">

                        <RiseOutlined />

                    </div>


                    <div className="ml-4">

                        <p className="text-[11px] md:text-sm font-semibold opacity-90">

                            PHỤC HỒI CHỨC NĂNG

                        </p>


                        <h1 className="text-[25px] md:text-3xl font-bold">

                            Theo dõi ROM

                        </h1>

                    </div>

                </div>


                <p className="text-[14px] md:text-base mt-4 opacity-90 leading-5 md:leading-normal max-w-3xl">

                    Ghi lại tầm vận động chủ động và thụ động của khớp
                    để theo dõi sự thay đổi theo thời gian.

                </p>

            </div>


            {/* HỒ SƠ */}

            {activeProfile ? (

                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mt-4">

                    <div className="flex items-center">

                        <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center">

                            <UserOutlined />

                        </div>


                        <div className="ml-3">

                            <p className="text-[11px] font-bold text-emerald-700 uppercase">

                                Đang ghi dữ liệu cho

                            </p>


                            <p className="font-bold text-gray-900">

                                {activeProfile.profile_name}

                            </p>


                            {activeProfile.relationship && (

                                <p className="text-xs text-gray-500 mt-0.5">

                                    {activeProfile.relationship}

                                </p>

                            )}

                        </div>

                    </div>

                </div>

            ) : (

                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mt-4">

                    <p className="font-semibold text-gray-800 text-sm">

                        Chưa chọn hồ sơ theo dõi

                    </p>


                    <p className="text-[13px] text-gray-600 mt-1">

                        Quay lại trang Phục hồi chức năng để chọn hoặc tạo hồ sơ trước khi lưu dữ liệu.

                    </p>


                    <a
                        href="/tienich/phuc-hoi-chuc-nang"
                        className="inline-block mt-2 text-emerald-600 font-semibold text-sm"
                    >

                        Chọn hồ sơ

                    </a>

                </div>

            )}


            {/* THÔNG BÁO */}

            {message && (

                <div className="bg-gray-50 border rounded-xl px-4 py-3 mt-4 text-[13px] text-gray-600">

                    {message}

                </div>

            )}


            {/* FORM */}

            <div className="bg-white border rounded-2xl p-4 md:p-6 mt-5">

                <h2 className="text-xl md:text-2xl font-bold text-gray-800">

                    Ghi nhận tầm vận động

                </h2>


                <p className="text-gray-500 text-sm mt-1">

                    Nhập kết quả ROM đã được đo hoặc đánh giá.

                </p>


                {/* KHỚP */}

                <div className="mt-5">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">

                        Khớp

                    </label>


                    <select
                        value={joint}
                        onChange={(e) =>
                            handleJointChange(
                                e.target.value
                            )
                        }
                        className="w-full h-12 border rounded-xl px-3 bg-white outline-none focus:border-blue-500"
                    >

                        <option value="vai">
                            Khớp vai
                        </option>

                        <option value="khuyu">
                            Khớp khuỷu
                        </option>

                        <option value="co-tay">
                            Khớp cổ tay
                        </option>

                        <option value="hang">
                            Khớp háng
                        </option>

                        <option value="goi">
                            Khớp gối
                        </option>

                        <option value="co-chan">
                            Khớp cổ chân
                        </option>

                    </select>

                </div>


                {/* BÊN */}

                <div className="mt-4">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">

                        Bên đo

                    </label>


                    <select
                        value={side}
                        onChange={(e) =>
                            setSide(e.target.value)
                        }
                        className="w-full h-12 border rounded-xl px-3 bg-white outline-none focus:border-blue-500"
                    >

                        <option value="phai">
                            Bên phải
                        </option>

                        <option value="trai">
                            Bên trái
                        </option>

                        <option value="hai-ben">
                            Hai bên
                        </option>

                    </select>

                </div>


                {/* VẬN ĐỘNG */}

                <div className="mt-4">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">

                        Động tác

                    </label>


                    <select
                        value={movement}
                        onChange={(e) =>
                            setMovement(e.target.value)
                        }
                        className="w-full h-12 border rounded-xl px-3 bg-white outline-none focus:border-blue-500"
                    >

                        {(movementOptions[joint] || []).map(
                            (item) => (

                                <option
                                    key={item.value}
                                    value={item.value}
                                >

                                    {item.label}

                                </option>

                            )
                        )}

                    </select>

                </div>


                {/* ROM CHỦ ĐỘNG / THỤ ĐỘNG */}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">

                    <div>

                        <label className="block text-sm font-semibold text-gray-700 mb-2">

                            ROM chủ động

                            <span className="font-normal text-gray-400 ml-1">
                                (độ)
                            </span>

                        </label>


                        <div className="relative">

                            <input
                                type="number"
                                min="0"
                                max="360"
                                step="1"
                                value={activeRom}
                                onChange={(e) =>
                                    setActiveRom(
                                        e.target.value
                                    )
                                }
                                placeholder="Ví dụ: 120"
                                className="w-full h-12 border rounded-xl px-3 pr-12 outline-none focus:border-blue-500"
                            />


                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">

                                °

                            </span>

                        </div>

                    </div>


                    <div>

                        <label className="block text-sm font-semibold text-gray-700 mb-2">

                            ROM thụ động

                            <span className="font-normal text-gray-400 ml-1">
                                (độ)
                            </span>

                        </label>


                        <div className="relative">

                            <input
                                type="number"
                                min="0"
                                max="360"
                                step="1"
                                value={passiveRom}
                                onChange={(e) =>
                                    setPassiveRom(
                                        e.target.value
                                    )
                                }
                                placeholder="Ví dụ: 140"
                                className="w-full h-12 border rounded-xl px-3 pr-12 outline-none focus:border-blue-500"
                            />


                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">

                                °

                            </span>

                        </div>

                    </div>

                </div>


                <p className="text-xs text-gray-400 mt-2">

                    Có thể nhập một hoặc cả hai kết quả tùy lần đánh giá.

                </p>


                {/* ĐAU */}

                <div className="mt-5 bg-gray-50 border rounded-2xl p-4">

                    <label className="flex items-center cursor-pointer">

                        <input
                            type="checkbox"
                            checked={painPresent}
                            onChange={(e) => {

                                const checked =
                                    e.target.checked;

                                setPainPresent(checked);

                                if (!checked) {
                                    setPainAngle("");
                                }
                            }}
                            className="mr-3"
                        />


                        <span className="text-sm font-semibold text-gray-700">

                            Có đau trong khi đánh giá ROM

                        </span>

                    </label>


                    {painPresent && (

                        <div className="mt-4">

                            <label className="block text-sm font-semibold text-gray-700 mb-2">

                                Góc xuất hiện đau

                                <span className="font-normal text-gray-400 ml-1">
                                    (không bắt buộc)
                                </span>

                            </label>


                            <div className="relative">

                                <input
                                    type="number"
                                    min="0"
                                    max="360"
                                    step="1"
                                    value={painAngle}
                                    onChange={(e) =>
                                        setPainAngle(
                                            e.target.value
                                        )
                                    }
                                    placeholder="Nhập nếu đã được ghi nhận"
                                    className="w-full h-12 bg-white border rounded-xl px-3 pr-12 outline-none focus:border-blue-500"
                                />


                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">

                                    °

                                </span>

                            </div>

                        </div>

                    )}

                </div>


                {/* TƯ THẾ */}

                <div className="mt-4">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">

                        Tư thế khi đo

                        <span className="font-normal text-gray-400 ml-1">
                            (không bắt buộc)
                        </span>

                    </label>


                    <input
                        type="text"
                        value={position}
                        onChange={(e) =>
                            setPosition(
                                e.target.value
                            )
                        }
                        placeholder="Ghi lại tư thế đã sử dụng khi đo..."
                        className="w-full h-12 border rounded-xl px-3 outline-none focus:border-blue-500"
                    />

                </div>


                {/* DỤNG CỤ */}

                <div className="mt-4">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">

                        Dụng cụ đo

                        <span className="font-normal text-gray-400 ml-1">
                            (không bắt buộc)
                        </span>

                    </label>


                    <input
                        type="text"
                        value={measuringDevice}
                        onChange={(e) =>
                            setMeasuringDevice(
                                e.target.value
                            )
                        }
                        placeholder="Ví dụ: thước đo góc..."
                        className="w-full h-12 border rounded-xl px-3 outline-none focus:border-blue-500"
                    />

                </div>


                {/* GHI CHÚ */}

                <div className="mt-4">

                    <label className="block text-sm font-semibold text-gray-700 mb-2">

                        Ghi chú

                        <span className="font-normal text-gray-400 ml-1">

                            (không bắt buộc)

                        </span>

                    </label>


                    <textarea
                        value={note}
                        onChange={(e) =>
                            setNote(
                                e.target.value
                            )
                        }
                        placeholder="Thông tin bổ sung của lần đánh giá..."
                        rows={3}
                        className="w-full border rounded-xl px-3 py-3 outline-none focus:border-blue-500 resize-none"
                    />

                </div>


                {/* LƯU */}

                <button
                    type="button"
                    onClick={handleSave}
                    disabled={
                        saving ||
                        !activeProfile
                    }
                    className="w-full mt-5 h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >

                    <SaveOutlined className="mr-2" />

                    {saving
                        ? "Đang lưu..."
                        : "Lưu kết quả ROM"
                    }

                </button>

            </div>


            {/* LỊCH SỬ */}

            <div className="mt-7">

                <h2 className="text-xl md:text-2xl font-bold text-gray-800">

                    Lịch sử ROM

                </h2>


                <p className="text-gray-500 text-sm mt-1">

                    Các kết quả ROM của hồ sơ đang được chọn.

                </p>


                {loading ? (

                    <div className="bg-white border rounded-2xl text-center p-8 mt-4">

                        <p className="text-sm text-gray-500">

                            Đang tải dữ liệu...

                        </p>

                    </div>

                ) : history.length === 0 ? (

                    <div className="bg-white border rounded-2xl text-center p-8 mt-4">

                        <RiseOutlined className="text-3xl text-gray-300" />


                        <p className="font-semibold text-gray-700 mt-3">

                            Chưa có dữ liệu

                        </p>


                        <p className="text-sm text-gray-500 mt-1">

                            Kết quả ROM của hồ sơ này sẽ xuất hiện tại đây.

                        </p>

                    </div>

                ) : (

                    <div className="space-y-3 mt-4">

                        {history.map((item) => (

                            <div
                                key={item.id}
                                className="bg-white border rounded-2xl p-4"
                            >

                                <div className="flex items-start justify-between">

                                    <div className="flex-1 pr-3">

                                        <div className="flex flex-wrap items-center gap-2">

                                            <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-lg text-sm font-bold">

                                                {item.jointLabel}

                                            </span>


                                            <span className="bg-gray-50 text-gray-600 px-3 py-1 rounded-lg text-sm">

                                                {item.sideLabel}

                                            </span>

                                        </div>


                                        <p className="font-semibold text-gray-800 mt-3">

                                            {item.movementLabel}

                                        </p>


                                        {/* DỮ LIỆU MỚI */}

                                        {(
                                            item.activeRom !== null ||
                                            item.passiveRom !== null
                                        ) && (

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">

                                                {item.activeRom !== null && (

                                                    <div className="bg-blue-50 rounded-xl px-3 py-2">

                                                        <p className="text-xs text-gray-500">

                                                            ROM chủ động

                                                        </p>

                                                        <p className="text-xl font-bold text-blue-700">

                                                            {item.activeRom}°

                                                        </p>

                                                    </div>

                                                )}


                                                {item.passiveRom !== null && (

                                                    <div className="bg-cyan-50 rounded-xl px-3 py-2">

                                                        <p className="text-xs text-gray-500">

                                                            ROM thụ động

                                                        </p>

                                                        <p className="text-xl font-bold text-cyan-700">

                                                            {item.passiveRom}°

                                                        </p>

                                                    </div>

                                                )}

                                            </div>

                                        )}


                                        {/* DỮ LIỆU CŨ */}

                                        {(
                                            item.activeRom === null &&
                                            item.passiveRom === null &&
                                            item.legacyDegree !== null
                                        ) && (

                                            <div className="bg-gray-50 rounded-xl px-3 py-2 mt-3">

                                                <p className="text-xs text-gray-500">

                                                    Kết quả ROM đã lưu trước đây

                                                </p>

                                                <p className="text-xl font-bold text-blue-600">

                                                    {item.legacyDegree}°

                                                </p>

                                                <p className="text-[11px] text-gray-400 mt-1">

                                                    Bản ghi cũ chưa phân biệt ROM chủ động và ROM thụ động.

                                                </p>

                                            </div>

                                        )}


                                        {item.painPresent && (

                                            <div className="bg-rose-50 rounded-xl px-3 py-2 mt-3">

                                                <p className="text-[13px] text-rose-700 font-semibold">

                                                    Có ghi nhận đau khi đánh giá

                                                </p>


                                                {item.painAngle !== null && (

                                                    <p className="text-[13px] text-gray-600 mt-1">

                                                        Góc xuất hiện đau: {item.painAngle}°

                                                    </p>

                                                )}

                                            </div>

                                        )}


                                        {(item.position ||
                                            item.measuringDevice) && (

                                            <div className="bg-gray-50 rounded-xl px-3 py-2 mt-3 space-y-1">

                                                {item.position && (

                                                    <p className="text-[13px] text-gray-600">

                                                        <strong>Tư thế:</strong>{" "}
                                                        {item.position}

                                                    </p>

                                                )}


                                                {item.measuringDevice && (

                                                    <p className="text-[13px] text-gray-600">

                                                        <strong>Dụng cụ:</strong>{" "}
                                                        {item.measuringDevice}

                                                    </p>

                                                )}

                                            </div>

                                        )}


                                        <p className="text-xs text-gray-400 mt-2">

                                            {item.time} - {item.date}

                                        </p>


                                        {item.note && (

                                            <div className="bg-gray-50 rounded-xl px-3 py-2 mt-3">

                                                <p className="text-[13px] text-gray-500">

                                                    {item.note}

                                                </p>

                                            </div>

                                        )}

                                    </div>


                                    <button
                                        type="button"
                                        onClick={() =>
                                            handleDelete(
                                                item.id
                                            )
                                        }
                                        className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center flex-shrink-0"
                                        aria-label="Xóa kết quả ROM"
                                    >

                                        <DeleteOutlined />

                                    </button>

                                </div>

                            </div>

                        ))}

                    </div>

                )}

            </div>


            {/* LƯU Ý */}

            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mt-7">

                <p className="font-semibold text-gray-800 text-sm">

                    Về theo dõi ROM

                </p>


                <p className="text-[13px] text-gray-600 leading-5 mt-1">

                    Công cụ này dùng để ghi lại kết quả tầm vận động
                    đã được đo hoặc đánh giá. ROM chủ động và ROM thụ động
                    được lưu riêng để theo dõi theo thời gian.

                </p>


                <p className="text-[13px] text-gray-500 leading-5 mt-2">

                    Website không tự đưa ra tầm vận động bình thường,
                    không tự chẩn đoán giới hạn vận động và không thay thế
                    đánh giá chuyên môn.

                </p>

            </div>

        </div>
    );
}